import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const isValidDate = (year: string, month: string, day: string) => {
  if (!year || !month || !day) return false
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
  return (
    date.getFullYear() === parseInt(year) &&
    date.getMonth() === parseInt(month) - 1 &&
    date.getDate() === parseInt(day)
  )
}
// Date of birth validation and auto-navigation
export const validateAge = (year: string, month: string, day: string) => {
  if (!year || !month || !day) return { isValid: false, error: '' }

  const birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--
  }

  if (birthDate > today) {
    return { isValid: false, error: 'Date of birth cannot be in the future' }
  }

  if (age < 18) {
    return { isValid: false, error: 'You must be at least 18 years old' }
  }

  if (age > 120) {
    return { isValid: false, error: 'Please enter a valid date of birth' }
  }

  return { isValid: true, error: '' }
}

// Occupation validation
export const validateOccupationDetails = (details: string) => {
  const trimmed = details.trim()
  const letterCount = (trimmed.match(/[a-zA-Z]/g) || []).length

  if (trimmed.length < 2) {
    return { isValid: false, error: 'Please enter at least 2 characters' }
  }

  if (letterCount < 2) {
    return { isValid: false, error: 'Please enter at least 2 letters' }
  }

  if (trimmed.length > 50) {
    return { isValid: false, error: 'Please keep it under 50 characters' }
  }

  return { isValid: true, error: '' }
}

// Name validation - only allow letters, spaces, hyphens, and apostrophes
export const validateName = (name: string) => {
  const nameRegex = /^[a-zA-Z\s'-]+$/
  if (!name.trim()) {
    return { isValid: false, error: 'Name is required' }
  }
  if (!nameRegex.test(name.trim())) {
    return {
      isValid: false,
      error: 'Name can only contain letters, spaces, hyphens, and apostrophes',
    }
  }
  if (name.trim().length < 2) {
    return { isValid: false, error: 'Name must be at least 2 characters' }
  }
  return { isValid: true, error: '' }
}

// Capitalize first letter of each word in name
export const capitalizeFirstName = (name: string): string => {
  if (!name || name.trim().length === 0) return name
  const trimmed = name.trim()
  return trimmed
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Format time remaining display
export const formatTimeRemaining = (seconds: number): string => {
  if (seconds <= 0) return 'Expired'

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`
  } else {
    return `${remainingSeconds}s`
  }
}

// Helper function to clean amount values (remove commas and leading zeros)
export const cleanAmountForAPI = (amount: string): string => {
  if (!amount) return amount

  // Remove commas first
  let cleaned = amount.replace(/,/g, '')

  // Remove leading zeros, but preserve single zero before decimal point
  // Examples: "020.20" -> "20.20", "00.50" -> "0.50", "000" -> "0"
  cleaned = cleaned.replace(/^0+(?=\d)/, '')

  // If we removed all digits and only have a decimal point, add a leading zero
  if (cleaned.startsWith('.')) {
    cleaned = '0' + cleaned
  }

  // If the string is empty after cleaning, return "0"
  if (cleaned === '') {
    cleaned = '0'
  }

  return cleaned
}
